import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Autocomplete, Avatar, Box, TextField, Typography } from "@mui/material";
import { fetchAllSessions } from "../../../actions/sessionActions";

/**
 * Autocomplete that selects from the full list of sessions, excluding any
 * session_ids already present in `excludeIds`. Filters to category- /
 * subcategory-parented sessions only — chapter, audiobook, series, and
 * challenge sessions are not eligible for the curated featured lists.
 * On selection it calls onAdd with the chosen session object and immediately
 * clears its own input.
 */
const SessionAddPicker = ({ excludeIds = [], onAdd, label = "Add session" }) => {
  const dispatch = useDispatch();
  const allSessions = useSelector(state => state.sessions.all_sessions);
  const isLoading = useSelector(state => state.sessions.isLoading);

  const [inputValue, setInputValue] = useState("");
  const [value, setValue] = useState(null);

  useEffect(() => {
    if (!allSessions) {
      dispatch(fetchAllSessions());
    }
  }, [dispatch, allSessions]);

  const excludedSet = useMemo(() => new Set(excludeIds), [excludeIds]);

  const options = useMemo(() => {
    if (!Array.isArray(allSessions)) return [];
    return allSessions.filter(s => {
      if (excludedSet.has(s.session_id)) return false;
      // Exclude session types that don't belong in the curated lists.
      if (s.session_is_chapter) return false;
      if (s.challenge_id) return false;
      if (s.series_id) return false;
      if (s.subcategory_id && s.subcategory_is_book) return false;
      // Must have a real parent (category or non-book subcategory).
      return Boolean(s.category_id || s.subcategory_id);
    });
  }, [allSessions, excludedSet]);

  // Prefer subcategory title (more specific) over category title when both are set.
  const parentLabel = session => session.subcategory_title || session.category_title || "";

  return (
    <Autocomplete
      value={value}
      onChange={(_event, newValue) => {
        if (newValue) {
          onAdd(newValue);
          // Clear immediately so the picker is ready for the next add
          setValue(null);
          setInputValue("");
        } else {
          setValue(null);
        }
      }}
      inputValue={inputValue}
      onInputChange={(_event, newInput) => setInputValue(newInput)}
      options={options}
      loading={isLoading}
      getOptionLabel={option => option?.session_name || ""}
      isOptionEqualToValue={(option, val) => option.session_id === val?.session_id}
      filterOptions={(opts, { inputValue: q }) => {
        const lc = q.trim().toLowerCase();
        if (!lc) return opts.slice(0, 100);
        return opts.filter(o => (o.session_name || "").toLowerCase().includes(lc)).slice(0, 100);
      }}
      renderOption={(props, option) => {
        const parent = parentLabel(option);
        const subtext = [parent, option.author_name].filter(Boolean).join(" · ");
        return (
          <Box component="li" {...props} key={option.session_id} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Avatar
              src={option.session_image || undefined}
              alt={option.session_name}
              variant="rounded"
              slotProps={{ img: { loading: "lazy" } }}
              sx={{ width: 32, height: 32, bgcolor: "grey.200" }}
            />
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography variant="body2">{option.session_name}</Typography>
              {subtext && (
                <Typography variant="caption" color="text.secondary">
                  {subtext}
                </Typography>
              )}
            </Box>
          </Box>
        );
      }}
      renderInput={params => <TextField {...params} label={label} placeholder="Search by session name" fullWidth />}
    />
  );
};

export default SessionAddPicker;
